export interface AdditionalProductDraftSection {
  config: {
    id: string;
    additionalProductListQuantity: number;
    setAdditionalProductListQuantity?(quantity: number): void;
  };
}

export interface AdditionalProductCategoryGroup<TKey extends string, TSection extends AdditionalProductDraftSection> {
  key: TKey;
  sections: TSection[];
}

export interface AdditionalProductsTableItem {
  quantity: number;
  positionId?: string;
}

export function createAdditionalProductDrafts<T extends AdditionalProductDraftSection>(sections: T[]) {
  return Object.fromEntries(sections.map((section) => [section.config.id, section.config.additionalProductListQuantity]));
}

export function normalizeAdditionalProductQuantity(quantity: number | string | null | undefined) {
  const parsedQuantity = Number(quantity);
  return Number.isFinite(parsedQuantity) ? Math.max(0, Math.floor(parsedQuantity)) : 0;
}

export function getAdditionalProductDraftQuantity<T extends AdditionalProductDraftSection>(
  editMode: boolean,
  sections: T[],
  drafts: Record<string, number>,
  configId: string,
) {
  return editMode
    ? (drafts[configId] ?? 0)
    : (sections.find((section) => section.config.id === configId)?.config.additionalProductListQuantity ?? 0);
}

export function getVisibleAdditionalProductSections<T extends AdditionalProductDraftSection>(
  editMode: boolean,
  sections: T[],
  drafts: Record<string, number>,
) {
  if (!editMode) {
    return sections;
  }

  return sections.filter((section) => getAdditionalProductDraftQuantity(true, sections, drafts, section.config.id) > 0);
}

export function groupVisibleAdditionalProductSections<T extends AdditionalProductDraftSection, TKey extends string>(
  editMode: boolean,
  sections: T[],
  drafts: Record<string, number>,
  getKey: (section: T) => TKey,
): AdditionalProductCategoryGroup<TKey, T>[] {
  const groupedSections = new Map<TKey, T[]>();

  sections.forEach((section) => {
    const key = getKey(section);
    const currentSections = groupedSections.get(key) ?? [];
    if (!editMode || getAdditionalProductDraftQuantity(true, sections, drafts, section.config.id) > 0) {
      currentSections.push(section);
    } else if (!groupedSections.has(key)) {
      groupedSections.set(key, []);
      return;
    }
    groupedSections.set(key, currentSections);
  });

  return Array.from(groupedSections.entries()).map(([groupKey, grouped]) => ({
    key: groupKey,
    sections: grouped,
  }));
}

export function getAdditionalProductsTableDraftQuantity<T extends AdditionalProductDraftSection>(
  editMode: boolean,
  drafts: Record<string, number>,
  section: T,
) {
  if (editMode) {
    return drafts[section.config.id] ?? 0;
  }

  return section.config.additionalProductListQuantity;
}

export function getAdditionalProductsTableProductQuantity<
  TSection extends AdditionalProductDraftSection,
  TProduct extends AdditionalProductsTableItem,
>(editMode: boolean, drafts: Record<string, number>, product: TProduct, section: TSection) {
  return getAdditionalProductsTableDraftQuantity(editMode, drafts, section) * product.quantity;
}

export function sortAdditionalProductsByPositionId<TItem extends { positionId?: string }>(products: TItem[]) {
  return [...products].sort((firstProduct, secondProduct) =>
    (firstProduct.positionId ?? "").localeCompare(secondProduct.positionId ?? "", undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

export function getChangedAdditionalProductSections<T extends AdditionalProductDraftSection>(
  sections: T[],
  drafts: Record<string, number>,
) {
  return sections.filter((section) => drafts[section.config.id] !== section.config.additionalProductListQuantity);
}

export function createAdditionalProductRollbackState<T extends AdditionalProductDraftSection>(sections: T[]) {
  return new Map(sections.map((section) => [section.config.id, section.config.additionalProductListQuantity]));
}

export function applyAdditionalProductDrafts<T extends AdditionalProductDraftSection>(
  sections: T[],
  drafts: Record<string, number>,
) {
  sections.forEach((section) => {
    section.config.setAdditionalProductListQuantity?.(drafts[section.config.id] ?? 0);
  });
}

export function rollbackAdditionalProductDrafts<T extends AdditionalProductDraftSection>(
  sections: T[],
  rollbackState: Map<string, number>,
) {
  sections.forEach((section) => {
    section.config.setAdditionalProductListQuantity?.(rollbackState.get(section.config.id) ?? 0);
  });
}
